import React, { useState } from 'react';
import { FormFieldLabel, Select, Table } from '@vwfs-bronson/bronson-react';
import './style.css';
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  ICustomerTrip, IBillingError,
  Currency,
} from "../../../models/customer-data.interface";
import InvoicesModal from '../../base/invoices-modal';
import { formatter } from '../../../utils/time-utils';
import ErrorsModal from '../../base/errors-modal';
import { selectNotificationStore } from '../../../redux/slices/notification-slice';
import { selectTrips } from '../../../redux/slices/customer-slice';
import TooltipInfoIcon from '../../base/tooltip-info-icon';
import MessageModal from '../../base/message-modal';
import InputModal from '../../base/input-modal';
import { fetchTripPayment, refundCustomerTrip } from '../../../redux/slices/customer-trips';
import TripDetailsModal from '../../base/trip-details-modal';
import { renderBillingInvoices } from '../../base/billing-invoices-label';
import { TripInvoiceData } from '../../base/billing-invoices-label/data/TripInvoiceData';
import Cookie from "js-cookie";
import { useSearchParams } from 'react-router-dom';
import InfoIconExtended from '../../base/info-icon-extended';
import { PayloadAction } from '@reduxjs/toolkit';

const handleOpenTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function TripHistoryDataView() {
  const dispatch = useAppDispatch();
  const notification = useAppSelector(selectNotificationStore());
  const trips = useAppSelector(selectTrips());
  const [searchParams] = useSearchParams();

  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [action, setAction] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState<ICustomerTrip>(null);
  const [errors, setErrors] = useState([]);
  const [refundAmount, setRefundAmount] = useState<number>(null);
  const [amountCurrency, setAmountCurrency] = useState<Currency>(null);
  const [totalTripAmount, setTotalTripAmount] = useState<number>(null);
  const [errorNotfication, setErrorNotification] = useState<string>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleOnClose = () => {
    setShowModal(false);
    setInvoices([]);
    setSelectedTrip(null);
  }

  const handleOnModalShow = (invoices) => {
    setInvoices(invoices);
    setShowModal(true);
  }


  const handleActionClose = () => {
    setAction(null);
    setShowActionModal(false);
    setShowRefundModal(false);
    setShowDetailsModal(false);
    setSelectedTrip(null);
  }

  const handleRefundActionClose = () => {
    handleActionClose();

    setErrorNotification(null);
  }


  const handleOnAction = (trip: ICustomerTrip, act) => {
    setSelectedTrip(trip);

    if (action != act) {
      if (act === 'Show_Details') {
        setShowDetailsModal(true);
      } else {
        setShowDetailsModal(false);
        setShowRefundModal(true);
        setAction(act);

        const tripTotalAmount = trip.billings.filter(b => b.type !== 'REFUND' && b.state === 'COMPLETED').reduce((acc, val) => acc += (val?.totalAmount?.grossAmount || 0), 0);
        const refundedTotalAmount = trip.billings.filter(b => b.type === 'REFUND' && (b.state === 'COMPLETED' || b.state === 'INITIATED')).reduce((acc, val) => acc += (val?.refundAmount?.grossAmount || 0), 0);
        const decimalTotalAmount = Math.round((tripTotalAmount - refundedTotalAmount) * 100) / 100;

        setRefundAmount(decimalTotalAmount);
        setTotalTripAmount(decimalTotalAmount)

        setAmountCurrency(trip.billings[0]?.totalAmount?.currency || 'EUR');
      }
    }
  }

  const handleActionTripPayment = () => {
    handleActionClose();

    if (action === 'Refund') {
      handleOnTripRefund();
    }
  }

  const handleOnTripRefund = () => {
    dispatch(
      refundCustomerTrip({
        trippaymentId: selectedTrip.id,
        serviceProviderId: selectedTrip.serviceProviderId,
        refundAmount: {
          grossAmount: refundAmount,
          currency: amountCurrency,
        }
      })
    )
  }

  const handleOnErrorClose = () => {
    setShowErrorModal(false);
    setErrors([]);
  }

  const handleOnErrorModalShow = (errors) => {
    setErrors(errors);
    setShowErrorModal(true);
  }

  const showConfirmationModal = () => {
    if (!refundAmount || !amountCurrency) {
      setErrorNotification('Refund amount not properly entered.')
      return
    }

    if (refundAmount > totalTripAmount) {
      setErrorNotification("Refund amount can't higher than the total invoice amount.");
      return
    }

    setShowRefundModal(false);
    setShowActionModal(true);
  }

  function renderBillingErrors(invoiceNumber: string, errors: IBillingError[]) {
    if (!errors?.length) {
      return null;
    }

    return (
      <ul className='row-ul'>
        {
          <li key={invoiceNumber + '_li_error'} className='row-li'>
            <span key={invoiceNumber + '_span_error'} className='row-li-inline'>
              <a onClick={() => handleOnErrorModalShow(errors)}>
                Errors ({errors.length})
              </a>
            </span>
          </li>
        }
      </ul>
    )
  }

  function isBifrostUserAgent(): boolean {
    const tokenType = Cookie.get('backoffice');
    const tokenTypeNormalized = tokenType ? tokenType.toLocaleUpperCase() : 'UNKNOWN';

    return tokenTypeNormalized === 'BIFROST';
  }

  function renderActionButtons(trip: ICustomerTrip) {
    let isRefundable = trip?.state === 'COMPLETED' && ['MSP_Emulator', 'Dott'].includes(trip.serviceProviderId) && isBifrostUserAgent();

    const tripTotalAmount = trip.billings.filter(b => b.type !== 'REFUND' && b.state === 'COMPLETED').reduce((acc, val) => acc += (val?.totalAmount?.grossAmount || 0), 0);
    const refundedTotalAmount = trip.billings.filter(b => b.type === 'REFUND' && (b.state === 'COMPLETED' || b.state === 'INITIATED')).reduce((acc, val) => acc += (val?.refundAmount?.grossAmount || 0), 0);
    const decimalTotalAmount = Math.round((tripTotalAmount - refundedTotalAmount) * 100) / 100;

    isRefundable = isRefundable && decimalTotalAmount !== 0;

    return (
      <Select onChange={(e) => handleOnAction(trip, e.target.value)} className='action-combo' value={'Select'} testId={`action-trip-${trip?.id}-btn`}>
        <Select.Item value='Select' testId={`select-trip-${trip?.id}-btn`}>Select</Select.Item>
        <Select.Item disabled={!isRefundable} value='Refund' testId={`refund-trip-${trip?.id}-btn`}>Refund</Select.Item>
        <Select.Item value='Show_Details' testId={`details-trip-${trip?.id}-btn`}>Show Details</Select.Item>
      </Select>
    );
  }

  function renderItem(trip: ICustomerTrip) {
    if (!trip) {
      return null;
    }

    const totalChargePaid = trip.billings.findIndex(item => item.state !== 'COMPLETED' && item.type === 'CHARGE') === -1;
    const isTotalFeePaid = trip.billings.findIndex(item => item.state !== 'COMPLETED' && item.type === 'FEE') === -1;

    const tripInvoiceData: TripInvoiceData = {
      state: trip.state,
      billings: trip.billings,
      serviceProviderId: trip.serviceProviderId,
      isTotalChargePaid: totalChargePaid,
      isTotalFeePaid: isTotalFeePaid,
      paymentId: trip.paymentId,
    }

    const amount = trip?.invoiceAmount ? `${trip.invoiceAmount.grossAmount} ${trip?.invoiceAmount.currency}` : '0 EUR';
    const mspTripIdentifier = trip.externalTripIdentifiers?.length > 0 ? trip.externalTripIdentifiers[0]?.id : undefined;

    return (
      <Table.Tr key={trip.id} className={mspTripIdentifier === searchParams.get('tripId') ? 'table-row row-selected' : 'table-row'}>
        <Table.Td>
          <>
            {mspTripIdentifier ?
              <>
                {mspTripIdentifier.substring(0, 9) + '...'}
                <InfoIconExtended label={trip.externalTripIdentifiers[0].id} />
              </> : '-'}
          </>
        </Table.Td>
        <Table.Td>{trip.serviceProviderId}</Table.Td>
        <Table.Td>{trip.tripPeriod ? formatter.format(new Date(trip.tripPeriod.startTimestamp)) :
          (trip.reservation?.reservationPeriod?.startTimestamp ? formatter.format(new Date(trip.reservation.reservationPeriod.startTimestamp)) : '-')}</Table.Td>
        <Table.Td>{trip.state}{trip.state === 'CANCELED'}</Table.Td>
        <Table.Td>{trip.travelledMileage.value} {trip.travelledMileage.unit}</Table.Td>
        <Table.Td>{trip.vehicle.vehicle.model.vehicleType}</Table.Td>
        <Table.Td>{amount}</Table.Td>
        <Table.Td>{trip.billings.length > 0 ?
          <>
            {renderBillingInvoices(tripInvoiceData, handleOnModalShow)}
            {renderBillingErrors(trip.id, trip.errors)}
          </> : '-'}</Table.Td>
        <Table.Td>{renderActionButtons(trip)}</Table.Td>
      </Table.Tr>
    );
  }

  async function fetchPdfurlHandler(invoiceNumber: string) {
    const selectedPayment = trips.data.find(t => t.billings.find(b => b.invoiceNumber === invoiceNumber));
    const selectedInvoice = selectedPayment?.billings.find(b => b.invoiceNumber === invoiceNumber);

    if (selectedInvoice?.invoicePDFURL) {
      handleOpenTab(selectedInvoice.invoicePDFURL)
      return;
    }

    const response = await dispatch(fetchTripPayment({
      customerId: selectedPayment.customerId,
      tripId: selectedPayment.id
    })) as PayloadAction<ICustomerTrip>;

    if (response.payload && response.payload.billings.length > 0) {
      const url = response.payload.billings[0].invoicePDFURL;
      if (url) handleOpenTab(url);
    }
  }

  return (
    <>
      {trips.presentationData && trips.presentationData?.length > 0 && (
        <Table colored={true} testId="trip-history-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>MSP Trip ID</Table.Th>
              <Table.Th>MSP Name</Table.Th>
              <Table.Th>Start Time</Table.Th>
              <Table.Th>Trip State
                <TooltipInfoIcon>
                  <b>Upcoming</b>: Await 3DS challenge<br />
                  <b>Vehicle Ready</b>: All preconditions are fulfilled. User can start a trip.<br />
                  <b>In Trip</b>: User is in an active trip.<br />
                  <b>In Stopover</b>: User trip is paused. User can resume trip. <br />
                  <b>Completed</b>: User trip has ended.<br />
                  <b>Canceled</b>: User reservation was canceled.<br />
                </TooltipInfoIcon>
              </Table.Th>
              <Table.Th>Distance</Table.Th>
              <Table.Th>Vehicle type</Table.Th>
              <Table.Th>Gross Amount</Table.Th>
              <Table.Th>Invoices<TooltipInfoIcon testId="infoIconInvoicesData">Click on invoice for more details</TooltipInfoIcon></Table.Th>
              <Table.Th>Details & Actions</Table.Th>
            </Table.Tr >
          </Table.Thead >
          <Table.Tbody>{trips?.presentationData?.map((trip) => renderItem(trip))}</Table.Tbody>
        </Table >
      )
      }

      <InvoicesModal invoices={[...invoices]} showModal={showModal} handleOnClose={handleOnClose} handleFetchPdfUrl={fetchPdfurlHandler} />
      <ErrorsModal errors={[...errors]} showModal={showErrorModal} handleOnClose={handleOnErrorClose} />

      <MessageModal
        message={action && `Are you sure you want to ${action} this trip payment?`}
        showModal={showActionModal}
        handleOnClose={handleActionClose}
        handleOnConfirm={handleActionTripPayment}
        action='Yes'
        title={`${action} trip payment`} />

      <InputModal
        title='Refund trip payment'
        message={`Trip total gross amount is ${totalTripAmount} ${amountCurrency}`}
        showModal={showRefundModal}
        handleOnClose={handleRefundActionClose}
        handleOnConfirm={showConfirmationModal}
        handleOnInputChange={(value) => {
          if (value) setRefundAmount(parseFloat(value));
          else setRefundAmount(null);

          setErrorNotification(null);
        }}
        defaultInputValue={totalTripAmount}
        inputValue={refundAmount}
        inputType={'number'}
        errorNotfication={errorNotfication} />

      {
        ((!trips.presentationData || trips.presentationData?.length === 0) && trips?.status !== 'failed') && (
          <FormFieldLabel testId="noTripsLabel">No Trips for Customer</FormFieldLabel>)
      }

      {
        trips?.status === 'failed' && (
          <FormFieldLabel testId="errorTripsLabel">{notification.error}</FormFieldLabel>)
      }

      <TripDetailsModal
        title='Trip Details'
        showModal={showDetailsModal}
        trip={selectedTrip}
        handleOnModalClose={handleActionClose}
      />
    </>
  );
}
